import {
    AbstractMesh, Color3, CubeTexture,
    DefaultLoadingScreen,
    Engine, float, ImageSourceBlock,
    Mesh,
    NodeMaterial, PBRMaterial,
    Texture,
    Vector3
} from "@babylonjs/core";
import { ViewerScene } from "./viewerScene";
import { copyArray, Loader, MaterialOutput, MaterialParameter, MaterialTypeInput, MaterialTypeOutput } from "viewer-core";
import { LoadList, MaterialInput } from "viewer-core";
import { InputBlock } from "@babylonjs/core/Materials/Node/Blocks/Input/inputBlock";
import { Animation, ArcRotateCamera, ArcRotateCameraMouseWheelInput, ArcRotateCameraPointersInput, CubicEase, EasingFunction } from "@babylonjs/core";

let _canvas;
let engine: Engine;
let scene: ViewerScene;
let _assetsHostUrl: string;
let hasWood: boolean;
let currentPoele: any;
let materialTypesInputList: Array<MaterialTypeInput>;
let materialsInputList: any;
let materialTypesOutputList: Array<MaterialTypeOutput>;
let materialOutputList: Array<MaterialOutput>;
let renderLoop = () => {scene.render()};


export async function initializeViewer(canvas: HTMLCanvasElement, assetsHostUrl: string, poeleRef: string, onFinish: any) {
    if (!assetsHostUrl) {
        throw new Error("Pas d'url fournie pour les assets");
    }

    _assetsHostUrl = assetsHostUrl;

    const response = await fetch(_assetsHostUrl + "refs.json");
    response.json()
        .then(res => {
            materialsInputList = res.Materials;
            let poeles = res.Poeles;
            for(let i = 0; i < poeles.length; i++){
                if(poeles[i].ref.toLowerCase() == poeleRef.toLocaleLowerCase()){
                    currentPoele = poeles[i];
                    let materials = new Array<MaterialInput>;
                    materialTypesInputList = new Array<MaterialTypeInput>();
                    hasWood = currentPoele.wood;

                    // Types de matériaux
                    res.MaterialTypes.forEach(t => {
                        let type: MaterialTypeInput = {
                            type: t.type,
                            url: t.url
                        }
                        materialTypesInputList.push(type);
                    });

                    // Variation chargée par défaut si le poêle en a une
                    if(Object.keys(currentPoele.configs).length > 0){
                        materialsInputList.forEach(m => {
                            if(m.name == Object.keys(currentPoele.configs)[0]){
                                let defaultVariation: MaterialInput = {
                                    name: "Exte_change",
                                    type: m.type,
                                    parameters : copyArray(m.parameters)
                                }
                                materials.push(defaultVariation);
                            }
                        });
                    }

                    // Matériaux communs
                    for(let j = 0; j < res.AlwaysLoad.length; j++){
                        let mat = res.AlwaysLoad[j];
                        if(mat.name.toLowerCase() == "buche" && !hasWood){
                            continue;
                        } else {
                            let materialInput: MaterialInput = {
                                name: mat.name,
                                type: mat.type,
                                parameters: mat.parameters
                            }
                            materials.push(materialInput);
                        }
                    }
                    return new LoadList(currentPoele, materialTypesInputList, materials);
                }
            }
        })
        .then(loadList => {
            // Engine
            _canvas = canvas;
            if(!engine){
                engine = new Engine(_canvas);
            } else{
                engine.stopRenderLoop(renderLoop);
            }
            if(scene){
                scene.dispose();
            }
            scene = new ViewerScene(engine, _canvas);

            // Loading Screen
            engine.loadingUIBackgroundColor = "#ffffff";
            DefaultLoadingScreen.DefaultLogoUrl = _assetsHostUrl + "/logo-laudevco.png";

            Loader.load(loadList, _assetsHostUrl, scene).then(loadedList => {
                scene.initialize();

                let root = scene.getNodeByName("__root__") as Mesh;

                const transform = {
                    position: new Vector3(0, 0, 0),
                    scaling: new Vector3(-1, 1, 1),
                    rotation: new Vector3(0, Math.PI/2, 0)
                };

                if(loadList.currentPoele.transform) {
                    const t = loadList.currentPoele.transform;
                    if(t.position) transform.position = new Vector3(t.position.x, t.position.y, t.position.z);
                    if(t.rotation) transform.rotation = new Vector3(t.rotation.x, t.rotation.y, t.rotation.z);
                    if(t.scaling)  transform.scaling  = new Vector3(t.scaling.x, t.scaling.y, t.scaling.z);
                }
                
                root.position = transform.position;
                root.scaling = transform.scaling;
                root.rotation = transform.rotation;
                scene.addShadowCaster(root, true);

                engine.runRenderLoop(renderLoop);

                //scene.debugLayer.show(); //TODO() : à enlever avant build prod

                materialTypesOutputList = loadedList[1];

                setDefaultMaterials(loadedList);

                // Disable zoom by default
                disableZoomInputs();

                if(onFinish) {
                    onFinish();
                }
            });

            window.addEventListener("resize", () => {
                engine.resize();
            });
        });
}

// Set a new material on the "Exte" mesh
export function setNewMaterial(newMaterial: string){
    let isLoaded = false;
    let currentVariation = changeMeshVariation(newMaterial);

    materialOutputList.forEach(m => {
       if(newMaterial == m.name){
           setMaterial(currentVariation, m);

           isLoaded = true;
       }
    });

    if(!isLoaded){
        if(currentPoele.configs.hasOwnProperty(newMaterial)){
            let mat = materialsInputList.find(m => m.name == newMaterial);
            let newMaterialInput: MaterialInput = {
                name: mat.name,
                type: mat.type,
                parameters: copyArray(mat.parameters)
            }

            let loadList: LoadList = {
                currentPoele: null,
                materialTypes: materialTypesInputList,
                materials: [newMaterialInput]
            }

            Loader.load(loadList, _assetsHostUrl, scene).then(loadedList => {
                let newMaterialOutput: MaterialOutput = loadedList[2][0];
                materialOutputList.push(newMaterialOutput);

                setMaterial(currentVariation, newMaterialOutput);
            });
        };
    }
}

export function disableZoomInputs(disablePinchZoom = true) {
    const camera = scene.activeCamera as unknown as ArcRotateCamera;
    camera.inputs.remove(camera.inputs.attached.mousewheel);

    if(disablePinchZoom) {
        const pointersInput = camera.inputs.attached.pointers as ArcRotateCameraPointersInput;
        pointersInput.pinchZoom = false;
    }
}

export function enableZoomInputs() {
    const camera = scene.activeCamera as unknown as ArcRotateCamera;
    const wheelInput = new ArcRotateCameraMouseWheelInput()
    wheelInput.wheelPrecision = 100;
    camera.inputs.add(wheelInput);

    const pointersInput = camera.inputs.attached.pointers as ArcRotateCameraPointersInput;
    pointersInput.pinchZoom = true;
}

const zoomEase = new CubicEase();
zoomEase.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

export function zoom(offset: number, duration: number = 0.25) {
    const camera = scene.activeCamera as unknown as ArcRotateCamera;
    // We invert the offset before applying to the radius to make it more "natural":
    // + values means zoom in and - means zoom out
    let targetRadius = camera.radius - offset;

    if (targetRadius < camera.lowerRadiusLimit) {
        targetRadius = camera.lowerRadiusLimit;
    }
    if (targetRadius > camera.upperRadiusLimit) {
        targetRadius = camera.upperRadiusLimit;
    }

    camera.animations.forEach((a) => scene.stopAnimation(a));
    Animation.CreateAndStartAnimation('zoomAnimation', camera, "radius", 60, duration * 60, camera.radius, targetRadius, 0, zoomEase);
}

// Set the value of a parameter on a material, according to the type of value and the name of the parameter
function setMaterialParameter(param: MaterialParameter, material: NodeMaterial){
    let block = material.getBlockByName(param.name)
    if(block == null){
        console.warn("Attention, le paramètre " + param.name + " n'existe pas dans le matériau " + material.name);
        return;
    }

    switch (param.type){
        case "texture": {
            (block as ImageSourceBlock).texture = param.value as Texture;
            break;
        }
        case "float": {
            (block as InputBlock).value = param.value as float;
            break;
        }
        case "vector3": {
            (block as InputBlock).value = new Vector3(param.value.x, param.value.y, param.value.z);
            break;
        }
        case "color3": {
            (block as InputBlock).value = new Color3(param.value.r, param.value.g, param.value.b);
            break;
        }
    }
}

//Create a new material by cloning its material type and assigning its parameters
function setMaterial(meshName: string, materialOutput: MaterialOutput){
    let mesh = scene.getNodeByName(meshName) as Mesh;

    // PBRMaterials
    if(materialOutput.type == "PBRMaterial"){
        let meshMat = mesh.material as PBRMaterial;

        materialOutput.parameters.forEach(p => {
            switch(p.name){
                case "BaseColor": meshMat.albedoTexture = p.value;
                case "Normal": meshMat.bumpTexture = p.value;
                case "AORM": meshMat.metallicTexture = p.value;
            }
            meshMat.useRoughnessFromMetallicTextureAlpha = false;
            meshMat.useMetallnessFromMetallicTextureBlue = true;
            meshMat.useAmbientOcclusionFromMetallicTextureRed = true;
        });

        return;
    }

    // NodeMaterials
    let typeMaterial = materialTypesOutputList.find(t => t.type == materialOutput.type).material;

    let material = typeMaterial.clone(materialOutput.name);

    materialOutput.parameters.forEach(p => {
        setMaterialParameter(p, material);
    });
    material.forceCompilationAsync(mesh).then(() => {
        mesh.material = material;
    });
}

function setEnvironmentTexture(envTexture: CubeTexture){
    scene.environmentTexture = envTexture;
    scene.environmentIntensity = 0.3;
}


// Set the default materials that are loaded at the beginning
function setDefaultMaterials(loadedList: any){
    let models: Array<AbstractMesh> = loadedList[0];
    let materialOutputs = loadedList[2] as Array<MaterialOutput>;
    materialOutputList = materialOutputs;

    models.forEach(model => {
        if(model.material == null){
            return;
        }
        // Mesh variation hidden by default
        if(model.name == "Exte1" || model.name == "Exte2"){
            model.visibility = 0;
            return;
        }

        materialOutputs.forEach(material => {
            // La rgex permet d'ignore les .00x que rajoute blender aux noms de materials
            if(material.name.toLowerCase() == model.material.name.replace(/\.[0-9]+/i, '').toLowerCase()) {
                if(material.name.toLowerCase() == "inside" || material.name.toLocaleLowerCase() == "insidemetal") {
                    // Fire Position
                    let fire = scene.getMeshByName("Fire") as Mesh;
                    fire.computeWorldMatrix(true);
                    const fireBox = fire.getBoundingInfo().boundingBox;
                    let firePosition: MaterialParameter = {
                        name: "FirePosition",
                        type: "vector3",
                        value: {
                            x: fireBox.centerWorld.x,
                            // un peu plus bas que le centre si poêle à buches
                            y: hasWood ? fireBox.centerWorld.y - fireBox.extendSizeWorld.y / 4 : fireBox.centerWorld.y,
                            z: fireBox.centerWorld.z
                        }
                    }
                    material.parameters.push(firePosition);

                    // Fade distance of the fire light
                    let inteMesh = scene.getNodeByName("Inte") as Mesh;
                    inteMesh.computeWorldMatrix(true);
                    let boundingBox = inteMesh.getBoundingInfo().boundingBox;
                    let fadeDistance: MaterialParameter = {
                        name: "FadeDistance",
                        type: "float",
                        // Take the bigger of the extent
                        value: 1.73 * Math.max(boundingBox.extendSizeWorld.x, boundingBox.extendSizeWorld.y, boundingBox.extendSizeWorld.z)
                    }
                    material.parameters.push(fadeDistance);

                    // Color of the shadows
                    let shadowColor: MaterialParameter = {
                        name: "ShadowColor",
                        type: "color3",
                        value: {
                            r: 0/255,
                            g: 0/255,
                            b: 0/255
                        }
                    }
                    material.parameters.push(shadowColor);

                    // Color of the fire light
                    let lightColor: MaterialParameter = {
                        name: "LightColor",
                        type: "color3",
                        value: {
                            r: 255/255,
                            g: 168/255,
                            b: 82/255
                        }
                    }
                    material.parameters.push(lightColor);

                    // Intensity
                    let intensity: MaterialParameter = {
                        name: "Intensity",
                        type: "float",
                        value: 0.73
                    }
                    material.parameters.push(intensity);

                    // Frequency of the flickering
                    let frequency: MaterialParameter = {
                        name: "Frequency",
                        type: "float",
                        value: 17
                    }
                    material.parameters.push(frequency);
                }

                setMaterial(model.name, material);
            } else if(material.type == "env"){
                setEnvironmentTexture(material.parameters[0].value);
            }
        });
    });
}

// Set the current mesh according to the material
function changeMeshVariation(materialName: string): string{
    for(let i = 0; i < Object.keys(currentPoele.configs).length; i++){
        if(Object.keys(currentPoele.configs)[i] == materialName) {
            if(Object.values(currentPoele.configs)[i]){
                scene.getMeshByName("Exte").visibility = 0;
                if(scene.getMeshByName("Exte1") != null) {scene.getMeshByName("Exte1").visibility = 1}
                if(scene.getMeshByName("Exte2") != null) {scene.getMeshByName("Exte2").visibility = 1}
                return "Exte1";
            } else {
                scene.getMeshByName("Exte").visibility = 1;
                if(scene.getMeshByName("Exte1") != null) {scene.getMeshByName("Exte1").visibility = 0}
                if(scene.getMeshByName("Exte2") != null) {scene.getMeshByName("Exte2").visibility = 0}
                return "Exte";
            }
        }
    }
}

